/** @format */

import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Tabs,
  Tab,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import AnimationWithTitle from "./AnimationWithTitle";
import DataNotFound from "../animations/not-found.json";
import { TabContext, TabList, TabPanel } from "@mui/lab";

const InterviewQuestionByCategory = ({
  questions,
  advancedQuestions,
  intermediateQuestions,
  basicQuestions,
  advancedQuestionsLoading,
  intermediateQuestionsLoading,
  basicQuestionsLoading,
  selectedTab,
  setSelectedTab,
}) => {
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const levels = ["advanced", "intermediate", "basic"];
  const [expandedIndex, setExpandedIndex] = useState(null);
  const questionsPerPage = 10;

  const filteredQuestions = levels.map((level) =>
    (questions[level] || []).filter(
      (item) => item.question && item.question.toLowerCase().includes(searchText.toLowerCase())
    )
  );

  // Reset expanded state when changing pages
  useEffect(() => {
    setExpandedIndex(null);
  }, [currentPage]);

  const handleAccordionChange = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setCurrentPage(1);
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value);
    setCurrentPage(1);
    setExpandedIndex(null);
  };

  const filteredAdvancedQuestions =
    advancedQuestions?.filter((item) => item.question?.toLowerCase().includes(searchText)) || [];

  const filteredIntermediateQuestions =
    intermediateQuestions?.filter((item) => item.question?.toLowerCase().includes(searchText)) || [];

  const filteredBasicQuestions =
    basicQuestions?.filter((item) => item.question?.toLowerCase().includes(searchText)) || [];

  const currentAdvancedQuestions =
    filteredAdvancedQuestions?.slice(
      (currentPage - 1) * questionsPerPage,
      currentPage * questionsPerPage
    ) || [];

  const currentIntermediateQuestions =
    filteredIntermediateQuestions?.slice(
      (currentPage - 1) * questionsPerPage,
      currentPage * questionsPerPage
    ) || [];

  const currentBasicQuestions =
    filteredBasicQuestions?.slice(
      (currentPage - 1) * questionsPerPage,
      currentPage * questionsPerPage
    ) || [];

  const totalQuestions =
    selectedTab === "advanced"
      ? filteredAdvancedQuestions.length
      : selectedTab === "intermediate"
      ? filteredIntermediateQuestions.length
      : filteredBasicQuestions.length;

  const totalPages = Math.ceil(totalQuestions / questionsPerPage);

  return (
    <Box sx={{ maxWidth: "100%", mx: "auto", py: 4 }}>
      {/* Search Bar */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "center",
          mx: { xs: 0, sm: 3, md: 4 },
        }}
      >
        <TextField
          fullWidth
          placeholder="Search questions..."
          variant="outlined"
          sx={{ borderRadius: "22px !important" }}
          value={searchText}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Tabs */}
      <TabContext value={selectedTab}>
        <TabList
          variant="scrollable"
          value={selectedTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{
            overflowX: { xs: "auto", md: "visible" },
            whiteSpace: { xs: "nowrap", md: "normal" },
            "& .MuiTabs-flexContainer": {
              display: "flex",
              justifyContent: { xs: "flex-start", md: "center" },
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: "bold",
              minWidth: "100px",
              borderRadius: "8px",
              margin: "0 8px",
              padding: "10px 20px",
              border: "2px solid transparent",
            },
            "& .Mui-selected": {
              border: "4.5px solid rgba(0, 0, 0, 0.3)",
            },
          }}
        >
          <Tab
            label="🔥 Advanced"
            value="advanced"
            sx={{
              alignItems: "center",
              textAlign: "center",
              backgroundColor: "#FFA6A6",
              color: "#000",
              "&.Mui-selected": { backgroundColor: "#d32f2f", color: "#fff" },
            }}
          />
          <Tab
            label="⭐ Intermediate"
            value="intermediate"
            sx={{
              alignItems: "center",
              textAlign: "center",
              backgroundColor: "#FFD580",
              color: "#000",
              "&.Mui-selected": { backgroundColor: "#FFB300", color: "#fff" },
            }}
          />
          <Tab
            label="✨ Basic"
            value="basic"
            sx={{
              alignItems: "center",
              textAlign: "center",
              backgroundColor: "#A8E6A3",
              color: "#000",
              "&.Mui-selected": { backgroundColor: "#388E3C", color: "#fff" },
            }}
          />
        </TabList>

        <TabPanel value="advanced">
          <Box>
            {advancedQuestionsLoading ? (
              // 🔹 Skeleton UI when loading
              [...Array(10)].map((_, i) => (
                <Accordion
                  key={i}
                  sx={{
                    mb: 2,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0rem 0.25rem 0px #caedff",
                  }}
                >
                  <AccordionSummary>
                    <Skeleton variant="text" width="80%" height={25} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ border: "1px solid #eaf8ff", py: 2 }}>
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="85%" height={20} />
                    <Skeleton variant="text" width="80%" height={20} />
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <>
                {currentAdvancedQuestions?.length > 0 ? (
                  currentAdvancedQuestions?.map((item, index) => (
                    <Accordion
                      key={index}
                      expanded={expandedIndex === index}
                      onChange={() => handleAccordionChange(index)}
                      sx={{
                        mb: 2,
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: "none !important",
                        backgroundColor: "rgb(168 203 219 / 63%)",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                          {item.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ border: "1px solid #eaf8ff", py: 2 }}>
                        <Typography color="text.secondary" variant="subtitle2">
                          {item.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))
                ) : (
                  <AnimationWithTitle title="No questions found" animation={DataNotFound} />
                )}
              </>
            )}
          </Box>
        </TabPanel>
        <TabPanel value="intermediate">
          <Box>
            {intermediateQuestionsLoading ? (
              // 🔹 Skeleton UI when loading
              [...Array(10)].map((_, i) => (
                <Accordion
                  key={i}
                  sx={{
                    mb: 2,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0rem 0.25rem 0px #caedff",
                  }}
                >
                  <AccordionSummary>
                    <Skeleton variant="text" width="80%" height={25} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ border: "1px solid #eaf8ff", py: 2 }}>
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="85%" height={20} />
                    <Skeleton variant="text" width="80%" height={20} />
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <>
                {currentIntermediateQuestions?.length > 0 ? (
                  currentIntermediateQuestions?.map((item, index) => (
                    <Accordion
                      key={index}
                      expanded={expandedIndex === index}
                      onChange={() => handleAccordionChange(index)}
                      sx={{
                        mb: 2,
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: "none !important",
                        backgroundColor: "rgb(168 203 219 / 63%)",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                          {item.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ border: "1px solid #eaf8ff", py: 2 }}>
                        <Typography color="text.secondary" variant="subtitle2">
                          {item.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))
                ) : (
                  <AnimationWithTitle title="No questions found" animation={DataNotFound} />
                )}
              </>
            )}
          </Box>
        </TabPanel>
        <TabPanel value="basic">
          <Box>
            {basicQuestionsLoading ? (
              // 🔹 Skeleton UI when loading
              [...Array(10)].map((_, i) => (
                <Accordion
                  key={i}
                  sx={{
                    mb: 2,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0rem 0.25rem 0px #caedff",
                  }}
                >
                  <AccordionSummary>
                    <Skeleton variant="text" width="80%" height={25} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ border: "1px solid #eaf8ff", py: 2 }}>
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="85%" height={20} />
                    <Skeleton variant="text" width="80%" height={20} />
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <>
                {currentBasicQuestions?.length > 0 ? (
                  currentBasicQuestions?.map((item, index) => (
                    <Accordion
                      key={index}
                      expanded={expandedIndex === index}
                      onChange={() => handleAccordionChange(index)}
                      sx={{
                        mb: 2,
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: "none !important",
                        backgroundColor: "rgb(168 203 219 / 63%)",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                          {item.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ border: "1px solid #eaf8ff", py: 2 }}>
                        <Typography color="text.secondary" variant="subtitle2">
                          {item.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))
                ) : (
                  <AnimationWithTitle title="No questions found" animation={DataNotFound} />
                )}
              </>
            )}
          </Box>
        </TabPanel>
      </TabContext>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
          }}
        >
          <Button
            variant="outlined"
            disabled={currentPage === 1}
            startIcon={<ChevronLeftIcon />}
            onClick={() => setCurrentPage((prev) => prev - 1)}
          >
            Prev
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>
          <Button
            variant="outlined"
            disabled={currentPage === totalPages}
            endIcon={<ChevronRightIcon />}
            onClick={() => setCurrentPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default InterviewQuestionByCategory;
